// ============================================================================
// MOTOR ESTRATÉGICO: Análisis multi-módulo del Portafolio Docente
// Se alimenta EXCLUSIVAMENTE de resultados-nacionales.js
// ============================================================================

import { RESULTADOS_NACIONALES, getIndicadoresModulo } from './resultados-nacionales';

// --- Relaciones entre indicadores (impacto transversal) — Rúbricas 2026 ---
const RELACIONES = {
    // Módulo 1
    'indicador-1-1': { depende: [], impacta: ['indicador-1-2', 'indicador-2-2'], grupo: 'diseño' },
    'indicador-1-2': { depende: ['indicador-1-1'], impacta: ['indicador-2-1'], grupo: 'diseño' },
    'indicador-1-3': { depende: [], impacta: ['indicador-1-2', 'indicador-3-1'], grupo: 'fundamentación' },
    'indicador-2-1': { depende: ['indicador-1-2'], impacta: ['indicador-2-2', 'indicador-2-3'], grupo: 'monitoreo' },
    'indicador-2-2': { depende: ['indicador-2-1'], impacta: ['indicador-2-3'], grupo: 'monitoreo' },
    'indicador-2-3': { depende: ['indicador-2-2'], impacta: [], grupo: 'monitoreo' },
    'indicador-3-1': { depende: ['indicador-1-3'], impacta: [], grupo: 'reflexión' },
    // Módulo 2
    'indicador-m2-9': { depende: [], impacta: ['indicador-m2-10'], grupo: 'ambiente' },
    'indicador-m2-10': { depende: ['indicador-m2-9'], impacta: ['indicador-m2-12'], grupo: 'participación' },
    'indicador-m2-11': { depende: [], impacta: ['indicador-m2-12'], grupo: 'organización' },
    'indicador-m2-12': { depende: ['indicador-m2-10', 'indicador-m2-11'], impacta: ['indicador-m2-14'], grupo: 'aprendizaje-profundo' },
    'indicador-m2-13': { depende: [], impacta: ['indicador-m2-12'], grupo: 'experiencias' },
    'indicador-m2-14': { depende: ['indicador-m2-12'], impacta: [], grupo: 'retroalimentación' },
    'indicador-m2-15': { depende: [], impacta: [], grupo: 'equidad' },
    // Módulo 3 (4 indicadores vigentes en 2026)
    'indicador-m3-16': { depende: [], impacta: ['indicador-m3-18', 'indicador-m3-21'], grupo: 'colaboración' },
    'indicador-m3-18': { depende: ['indicador-m3-16'], impacta: ['indicador-m3-21'], grupo: 'diálogo' },
    'indicador-m3-21': { depende: ['indicador-m3-18'], impacta: ['indicador-m3-23'], grupo: 'aprendizaje-profesional' },
    'indicador-m3-23': { depende: ['indicador-m3-21'], impacta: [], grupo: 'evaluación-conjunta' },
};

function calcHoras(pctCritico) {
    if (pctCritico >= 60) return { min: 7, max: 10, promedio: 8.5 };
    if (pctCritico >= 50) return { min: 5, max: 8, promedio: 6.5 };
    if (pctCritico >= 30) return { min: 3, max: 5, promedio: 4 };
    return { min: 1.5, max: 3, promedio: 2 };
}

function calcDesgaste(pctCritico, grupo) {
    const base = pctCritico >= 60 ? 5 : pctCritico >= 50 ? 4 : pctCritico >= 30 ? 3 : 2;
    const bonus = (grupo === 'reflexión' || grupo === 'excelencia') ? 1 : grupo === 'evaluación' || grupo === 'retroalimentación' ? 0.5 : 0;
    return Math.min(Math.round((base + bonus) * 10) / 10, 5);
}

export function getAnalisisEstrategico(moduloId) {
    const entries = moduloId
        ? Object.entries(RESULTADOS_NACIONALES).filter(([, v]) => v.modulo === moduloId)
        : Object.entries(RESULTADOS_NACIONALES);

    return entries.map(([id, datos]) => {
        const pctCritico = datos.insatisfactorio + datos.basico;
        const rel = RELACIONES[id] || { depende: [], impacta: [], grupo: 'otro' };
        const horas = calcHoras(pctCritico);
        const desgaste = calcDesgaste(pctCritico, rel.grupo);
        const impactoTransversal = rel.impacta.length;

        let dificultad, semaforo;
        if (pctCritico >= 50) { dificultad = 'Alta'; semaforo = 'red'; }
        else if (pctCritico >= 20) { dificultad = 'Media'; semaforo = 'amber'; }
        else { dificultad = 'Baja'; semaforo = 'green'; }

        let categoria;
        if (pctCritico >= 50 && impactoTransversal >= 2) categoria = 'critico-alto-impacto';
        else if (pctCritico >= 50) categoria = 'critico';
        else if (impactoTransversal >= 2) categoria = 'arrastre';
        else if (pctCritico < 20) categoria = 'fortaleza';
        else categoria = 'estable';

        return {
            id, nombre: datos.nombre, tarea: datos.tarea, modulo: datos.modulo,
            numOficial: datos.numOficial,
            pctInsatisfactorio: datos.insatisfactorio, pctBasico: datos.basico,
            pctCompetente: datos.competente, pctDestacado: datos.destacado,
            pctCritico: Math.round(pctCritico * 10) / 10,
            totalDocentes: datos.totalDocentes,
            dificultad, semaforo, horas, desgaste, impactoTransversal,
            dependeDe: rel.depende, impactaA: rel.impacta, grupo: rel.grupo, categoria,
            lectura: datos.lecturaSistema, implicancia: datos.implicancia,
        };
    });
}

export function getEstadisticasModulo(indicadores) {
    if (!indicadores.length) return null;
    const totalHoras = indicadores.reduce((s, i) => s + i.horas.promedio, 0);
    const avgDesgaste = indicadores.reduce((s, i) => s + i.desgaste, 0) / indicadores.length;
    const criticos = indicadores.filter(i => i.pctCritico >= 50).length;
    const fortalezas = indicadores.filter(i => i.pctCritico < 20).length;
    const avgCompDest = indicadores.reduce((s, i) => s + i.pctCompetente + i.pctDestacado, 0) / indicadores.length;
    let nivelEstimado;
    if (avgCompDest >= 80) nivelEstimado = 'Competente-Destacado';
    else if (avgCompDest >= 50) nivelEstimado = 'Competente';
    else if (avgCompDest >= 30) nivelEstimado = 'Básico-Competente';
    else nivelEstimado = 'Básico';
    return {
        totalIndicadores: indicadores.length, totalHoras: Math.round(totalHoras * 10) / 10,
        desgastePromedio: Math.round(avgDesgaste * 10) / 10, criticos, fortalezas, nivelEstimado,
        impactoEstrategico: criticos >= 3 ? 'Alto' : criticos >= 1 ? 'Medio' : 'Bajo',
    };
}

export function getSimulacion(indicadores) {
    const total = indicadores.length;
    if (!total) return null;
    const conservador = { nombre: 'Conservador', descripcion: 'Sin preparación adicional, siguiendo tendencia nacional',
        destacado: indicadores.filter(i => i.pctDestacado >= 50).length,
        competente: indicadores.filter(i => i.pctCompetente >= 40 && i.pctDestacado < 50).length,
        basico: indicadores.filter(i => i.pctBasico >= 40).length, insatisfactorio: indicadores.filter(i => i.pctInsatisfactorio >= 20).length };
    conservador.basico += total - (conservador.destacado + conservador.competente + conservador.basico + conservador.insatisfactorio);
    const optimo = { nombre: 'Óptimo', descripcion: 'Con preparación estratégica usando esta plataforma',
        destacado: indicadores.filter(i => i.pctCritico < 20).length,
        competente: indicadores.filter(i => i.pctCritico >= 20 && i.pctCritico < 50).length,
        basico: indicadores.filter(i => i.pctCritico >= 50 && i.pctCritico < 70).length, insatisfactorio: 0 };
    optimo.competente += total - (optimo.destacado + optimo.competente + optimo.basico);
    const altoRendimiento = { nombre: 'Alto rendimiento', descripcion: 'Con esfuerzo focalizado en indicadores críticos',
        destacado: indicadores.filter(i => i.pctCritico < 30).length,
        competente: total - indicadores.filter(i => i.pctCritico < 30).length, basico: 0, insatisfactorio: 0 };
    const calcP = (e) => Math.round(((e.destacado * 4 + e.competente * 3 + e.basico * 2 + e.insatisfactorio) / (total * 4)) * 100);
    return [
        { ...conservador, probabilidad: calcP(conservador), color: 'slate' },
        { ...optimo, probabilidad: calcP(optimo), color: 'blue' },
        { ...altoRendimiento, probabilidad: calcP(altoRendimiento), color: 'emerald' },
    ];
}

export function getRecomendaciones(indicadores) {
    const sorted = [...indicadores].sort((a, b) => (b.pctCritico * (1 + b.impactoTransversal * 0.3)) - (a.pctCritico * (1 + a.impactoTransversal * 0.3)));
    const recomendaciones = [];
    const criticosAltoImpacto = sorted.filter(i => i.categoria === 'critico-alto-impacto');
    if (criticosAltoImpacto.length) recomendaciones.push({ tipo: 'prioridad-maxima', icono: 'target', titulo: 'Prioridad Máxima', mensaje: `Enfoca tu esfuerzo en: ${criticosAltoImpacto.map(i => i.nombre).join(', ')}. Son críticos Y mejoran otros indicadores.`, indicadores: criticosAltoImpacto.map(i => i.id), color: 'red' });
    const criticos = sorted.filter(i => i.categoria === 'critico');
    if (criticos.length) recomendaciones.push({ tipo: 'criticos', icono: 'target', titulo: 'Indicadores Críticos', mensaje: `${criticos.map(i => i.nombre).join(', ')}: alta dificultad nacional. Requieren atención prioritaria.`, indicadores: criticos.map(i => i.id), color: 'red' });
    const arrastre = sorted.filter(i => i.categoria === 'arrastre');
    if (arrastre.length) recomendaciones.push({ tipo: 'arrastre', icono: 'link', titulo: 'Efecto Dominó', mensaje: `Mejorar ${arrastre.map(i => i.nombre).join(', ')} impactará positivamente otros indicadores.`, indicadores: arrastre.map(i => i.id), color: 'blue' });
    const fortalezas = sorted.filter(i => i.categoria === 'fortaleza');
    if (fortalezas.length) recomendaciones.push({ tipo: 'no-sobresfuerzo', icono: 'check-circle', titulo: 'No sobre-invertir aquí', mensaje: `${fortalezas.map(i => i.nombre).join(', ')}: altas probabilidades de Competente/Destacado. Mantén nivel sin gastar energía extra.`, indicadores: fortalezas.map(i => i.id), color: 'green' });
    const totalHoras = indicadores.reduce((s, i) => s + i.horas.promedio, 0);
    const horasCriticos = sorted.filter(i => i.pctCritico >= 50).reduce((s, i) => s + i.horas.promedio, 0);
    recomendaciones.push({ tipo: 'equilibrio', icono: 'clock', titulo: 'Equilibrio Tiempo-Resultado', mensaje: `Tiempo estimado: ${Math.round(totalHoras)}h. Los indicadores críticos demandan ${Math.round(horasCriticos)}h (${totalHoras > 0 ? Math.round(horasCriticos / totalHoras * 100) : 0}%). Distribuye tu energía con inteligencia.`, indicadores: [], color: 'amber' });
    return recomendaciones;
}
